import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, Beaker, MapPin, FileCheck, FlaskConical, Star, Zap, Sparkles, Flame } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import ProductVial from "../components/ProductVial";
import BiotechBackground from "../components/BiotechBackground";
import Reviews from "../components/Reviews";
import CountdownTimer from "../components/CountdownTimer";
import usePageMeta from "../lib/usePageMeta";
import { useSiteSettings } from "../lib/site-settings";

const trustBadges = [
  { icon: MapPin, label: "UK Based" },
  { icon: Truck, label: "Next-Day Delivery" },
  { icon: ShieldCheck, label: "Purity Tested" },
  { icon: FileCheck, label: "Batch Documented" },
  { icon: FlaskConical, label: "Research Use Only" },
];

export default function Home() {
  // Homepage "Flash Sale" spotlight SKU — runtime setting (Admin → Settings), no deploy needed.
  const { spotlight_sku: SPOTLIGHT_SKU } = useSiteSettings();
  usePageMeta(
    "Peptigen Labs | UK Research Peptide Supplier | Next-Day Delivery",
    "Trusted UK supplier of research-use-only peptides for laboratories, universities and qualified scientific buyers. Independently purity tested, batch documented, same-day dispatch."
  );
  const [featured, setFeatured] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    api.get("/products/featured").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/products").then((r) => {
      setAllProducts(r.data);
      setCatalogue(r.data.filter((p) => !p.featured && p.category !== "Laboratory Reagents").slice(0, 8));
    }).catch(() => {});
  }, []);

  // Reta sale spotlight + new-arrival lineup
  const retaSale = useMemo(() => allProducts.find((p) => p.sku === SPOTLIGHT_SKU), [allProducts, SPOTLIGHT_SKU]);
  const newArrivals = useMemo(
    () => allProducts.filter((p) => p.new_arrival && p.sku !== SPOTLIGHT_SKU).slice(0, 6),
    [allProducts, SPOTLIGHT_SKU]
  );

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <BiotechBackground variant="hero" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 pg-fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00C2D6]/30 rounded-sm mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A6FF00] pg-pulse-glow" />
                <span className="pg-mono text-[10px] uppercase tracking-[0.28em] text-[#00C2D6]">
                  British Research Peptide Supply
                </span>
              </div>
              <h1
                className="text-[#F7FAFC] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
                style={{ fontFamily: "'Exo 2', sans-serif", fontWeight: 800, letterSpacing: "-0.02em" }}
                data-testid="hero-headline"
              >
                Precision <span className="text-[#00C2D6]">Peptide</span>
                <br />
                Science.
              </h1>
              <p className="mt-7 text-[#9CA8B8] text-lg max-w-2xl leading-relaxed">
                Premium research-use-only peptides held in UK stock, independently purity tested and dispatched the same working day for tracked next-day UK delivery. Trusted by researchers, laboratories and qualified scientific buyers across Britain.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/currently-available" className="pg-btn-primary" data-testid="hero-shop-cta">
                  Shop Current Stock <ArrowRight size={18} />
                </Link>
                <Link to="/quality" className="pg-btn-ghost" data-testid="hero-quality-cta">
                  View Quality Standards
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-4">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={14} className="text-[#A6FF00] fill-[#A6FF00]" />
                  ))}
                </div>
                <div className="text-xs text-[#9CA8B8]">
                  <span className="text-[#F7FAFC] pg-heading">4.9 / 5</span> from 140+ verified UK customers
                </div>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
                {trustBadges.map(({ icon: Icon, label }) => (
                  <div key={label} className="inline-flex items-center gap-2 text-[#9CA8B8] text-xs pg-mono uppercase tracking-[0.2em]">
                    <Icon size={14} className="text-[#00C2D6]" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Product showcase grid */}
            <div className="lg:col-span-6">
              <div className="grid grid-cols-3 gap-3">
                {featured.slice(0, 3).map((p, idx) => (
                  <Link
                    key={p.sku}
                    to={`/products/${p.sku}`}
                    className={`pg-card relative aspect-[3/4] group ${p.best_seller ? "pg-best-seller" : ""} ${idx === 0 ? "lg:-translate-y-2" : idx === 2 ? "lg:translate-y-4" : ""}`}
                    data-testid={`hero-product-${p.sku}`}
                  >
                    <div className="absolute inset-0 pb-16">
                      <ProductVial product={p} size="md" />
                    </div>
                    {p.best_seller && (
                      <div
                        className="absolute top-0 left-0 right-0 z-20 flex items-center justify-center gap-1 py-1"
                        style={{ background: "linear-gradient(90deg, #A6FF00 0%, #5EEAD4 50%, #00C2D6 100%)" }}
                      >
                        <span className="pg-mono text-[8px] uppercase tracking-[0.2em] text-[#0B1220] font-extrabold">
                          ★ Best Seller
                        </span>
                      </div>
                    )}
                    <div className={`absolute ${p.best_seller ? "top-6" : "top-2"} left-2 right-2 flex justify-between z-10`}>
                      <span className="pg-mono text-[8px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(11,18,32,0.85)", color: "#A6FF00", border: "1px solid rgba(166,255,0,0.3)" }}>
                        ● In Stock
                      </span>
                      {p.on_sale && (
                        <span
                          className="pg-mono text-[8px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm font-bold"
                          style={{ background: "#A6FF00", color: "#0B1220" }}
                        >
                          Sale
                        </span>
                      )}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-[#0B1220] via-[#0B1220]/95 to-transparent z-10">
                      <div className="text-[#F7FAFC] pg-heading text-sm leading-tight" style={{ fontFamily: "'Rajdhani'" }}>
                        {p.name}
                      </div>
                      <div className="flex items-baseline justify-between mt-0.5">
                        <span className="text-[#5EEAD4] text-[10px] pg-mono">{p.strength}</span>
                        <div className="flex items-baseline gap-1">
                          {p.compare_at_price && p.on_sale && (
                            <span className="text-[#9CA8B8] text-[10px] line-through pg-mono">£{p.compare_at_price.toFixed(0)}</span>
                          )}
                          <span className="text-[#A6FF00] pg-display text-sm font-bold" style={{ fontFamily: "'Exo 2'" }}>
                            £{p.price.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {[
                  { label: "Purity", value: "≥99%" },
                  { label: "Dispatch", value: "Same-day" },
                  { label: "Delivery", value: "Next-day" },
                ].map((s) => (
                  <div key={s.label} className="pg-card p-4 text-center">
                    <div className="pg-micro mb-1">{s.label}</div>
                    <div className="text-[#F7FAFC] pg-display text-xl" style={{ fontFamily: "'Exo 2'" }}>{s.value}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-center gap-2 px-4 py-2 border border-[#A6FF00]/30 bg-[#A6FF00]/[0.04] rounded-sm" data-testid="hero-authenticity">
                <span className="w-1.5 h-1.5 rounded-full bg-[#A6FF00] pg-pulse-glow" />
                <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#9CA8B8]">
                  Genuine Peptigen Labs · Sold direct at peptigenlabs.co.uk only
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reta Sale Spotlight — limited-time promo */}
      {retaSale && retaSale.on_sale && (
        <section className="relative" data-testid="reta-sale-spotlight">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div
              className="relative overflow-hidden border-2 rounded-sm"
              style={{
                borderColor: "rgba(166,255,0,0.5)",
                background: "linear-gradient(135deg, rgba(166,255,0,0.06) 0%, rgba(11,18,32,1) 50%, rgba(0,194,214,0.04) 100%)",
              }}
            >
              {/* Sweep accent */}
              <div
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{ background: "linear-gradient(90deg, #A6FF00 0%, #5EEAD4 50%, #00C2D6 100%)" }}
              />

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 lg:p-12 items-center">
                <div className="lg:col-span-7">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm mb-5"
                       style={{ background: "rgba(166,255,0,0.12)", border: "1px solid rgba(166,255,0,0.4)" }}>
                    <Flame size={12} className="text-[#A6FF00]" />
                    <span className="pg-mono text-[10px] uppercase tracking-[0.28em] text-[#A6FF00] font-bold">
                      Flash Sale · Ends Soon
                    </span>
                  </div>
                  <h2
                    className="text-[#F7FAFC] text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-5"
                    style={{ fontFamily: "'Exo 2', sans-serif", fontWeight: 800, letterSpacing: "-0.02em" }}
                    data-testid="reta-sale-headline"
                  >
                    {retaSale.name} <span className="text-[#A6FF00]">{retaSale.strength}</span>
                  </h2>
                  <p className="text-[#F7FAFC] text-base sm:text-lg max-w-xl leading-relaxed mb-6">
                    Our biggest discount of the year on one of the UK's most-requested research peptides.
                    Price auto-reverts to <span className="line-through text-[#9CA8B8]">£{(retaSale.compare_at_price ?? 199.99).toFixed(2)}</span> {retaSale.sale_ends_at ? "the moment the timer below hits zero" : "when the sale ends"}.
                  </p>

                  {/* COUNTDOWN */}
                  {retaSale.sale_ends_at && (
                    <div className="mb-6" data-testid="reta-sale-countdown-wrapper">
                      <div className="pg-mono text-[10px] uppercase tracking-[0.28em] text-[#9CA8B8] mb-2.5">
                        Sale ends in
                      </div>
                      <CountdownTimer targetIso={retaSale.sale_ends_at} />
                    </div>
                  )}

                  <div className="flex items-baseline gap-4 mb-6">
                    <span
                      className="pg-display text-5xl sm:text-6xl text-[#A6FF00] font-extrabold"
                      style={{ fontFamily: "'Exo 2'" }}
                      data-testid="reta-sale-price"
                    >
                      £{retaSale.price.toFixed(2)}
                    </span>
                    {retaSale.compare_at_price && (
                      <div className="flex flex-col">
                        <span className="text-[#9CA8B8] text-lg line-through pg-mono">
                          £{retaSale.compare_at_price.toFixed(2)}
                        </span>
                        <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#A6FF00] font-bold">
                          Save £{(retaSale.compare_at_price - retaSale.price).toFixed(2)} per vial
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <Link
                      to={`/products/${SPOTLIGHT_SKU}`}
                      className="pg-btn-primary"
                      data-testid="reta-sale-cta"
                    >
                      Claim This Price Now <ArrowRight size={18} />
                    </Link>
                    <Link to="/currently-available" className="pg-btn-ghost">
                      See all in stock
                    </Link>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-[11px] pg-mono uppercase tracking-[0.22em] text-[#9CA8B8]">
                    <span>● UK Stock</span>
                    <span>● Same-Day Dispatch</span>
                    <span>● 99%+ Purity</span>
                    <span>● Limited Quantity</span>
                  </div>
                </div>

                {/* Vial showcase */}
                <div className="lg:col-span-5">
                  <Link
                    to={`/products/${SPOTLIGHT_SKU}`}
                    className="block relative aspect-square max-w-[360px] mx-auto pg-card overflow-hidden group"
                    data-testid="reta-sale-vial"
                  >
                    <ProductVial product={retaSale} size="lg" />
                    <div
                      className="absolute top-4 left-4 px-2.5 py-1 rounded-sm pg-mono text-[9px] uppercase tracking-[0.22em] font-extrabold"
                      style={{ background: "#A6FF00", color: "#0B1220" }}
                    >
                      Flash Sale
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Just-Landed New Arrivals */}
      {newArrivals.length > 0 && (
        <section className="relative" data-testid="new-arrivals-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
              <div>
                <div className="inline-flex items-center gap-2 mb-3">
                  <Sparkles size={14} className="text-[#00C2D6]" />
                  <span className="pg-mono text-[11px] uppercase tracking-[0.28em] text-[#00C2D6]">
                    Just Landed
                  </span>
                </div>
                <h2
                  className="text-[#F7FAFC] text-3xl lg:text-4xl pg-display"
                  style={{ fontFamily: "'Exo 2'" }}
                  data-testid="new-arrivals-headline"
                >
                  New in the UK catalogue
                </h2>
                <p className="text-[#9CA8B8] mt-3 max-w-xl">
                  Fresh additions to the Peptigen Labs research lineup — independently
                  purity tested, dispatched from UK stock.
                </p>
              </div>
              <Link to="/products" className="pg-btn-ghost self-start" data-testid="view-new-arrivals">
                Browse all <ArrowRight size={16} />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {newArrivals.slice(0, 6).map((p) => (
                <ProductCard key={p.sku} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      <Reviews limit={6} />

      {/* Research Catalogue */}
      <section className="relative" data-testid="research-catalogue-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="pg-micro mb-3">// Research Catalogue</div>
              <h2 className="text-[#F7FAFC] text-3xl lg:text-4xl pg-display" style={{ fontFamily: "'Exo 2'" }}>
                Additional Research Peptides
              </h2>
              <p className="text-[#9CA8B8] mt-3 max-w-xl">
                Listed for reference. Availability may vary by batch.
              </p>
            </div>
            <Link to="/products" className="pg-btn-ghost self-start" data-testid="view-full-catalogue">
              View full catalogue <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {catalogue.map((p) => (
              <ProductCard key={p.sku} product={p} />
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: MapPin, title: "UK-Based, UK-Focused", body: "British research peptide supplier. UK-based stock, UK customer support, built for UK researchers and laboratories." },
              { icon: Truck, title: "Same-Day UK Dispatch", body: "Eligible orders placed before 14:00 are dispatched the same working day with tracked next-day delivery." },
              { icon: ShieldCheck, title: "Purity Tested · Batch Documented", body: "Every batch is independently purity tested. Certificate of Analysis available with each order." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="pg-card p-7">
                <Icon size={22} className="text-[#A6FF00] mb-4" />
                <h3 className="text-[#F7FAFC] text-xl mb-2 pg-heading" style={{ fontFamily: "'Rajdhani'" }}>
                  {title}
                </h3>
                <p className="text-[#9CA8B8] text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="border-y" style={{ borderColor: "rgba(0,194,214,0.18)", backgroundColor: "#080F1B" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center">
          <div className="pg-micro mb-3">Mandatory Compliance Notice</div>
          <p className="text-[#9CA8B8] max-w-3xl mx-auto leading-relaxed">
            All products are supplied strictly for laboratory research use only. Not for human
            consumption. Not intended to diagnose, treat, cure, or prevent any disease.
          </p>
        </div>
      </section>
    </div>
  );
}
